import { Search, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  SelectableProhibitedKeywordList,
  type SelectableKeywordItem,
} from "@/components/shared/SelectableProhibitedKeywordList";

interface GlobalKeywordsListProps {
  /** The sorted, search-filtered keywords. */
  filteredKeywords: SelectableKeywordItem[];
  /** The unfiltered count; picks the empty-state copy. */
  totalCount: number;
  isLoading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onRemove: (
    item: SelectableKeywordItem,
    callbacks: { onSuccess: () => void },
  ) => void;
  onRemoveMany: (ids: number[]) => void;
  /**
   * AUT-220: the id currently being deleted, not a blanket "a delete is running"
   * flag, so one slow delete never locks the rest of the list.
   */
  removingId: number | null;
}

// AUT-41 / AUT-431: the left-hand card of GlobalProhibitedKeywordsTab -- heading, search,
// then the shared list that owns badges, Multi Select, empty state and confirmations.
export function GlobalKeywordsList({
  filteredKeywords,
  totalCount,
  isLoading,
  search,
  onSearchChange,
  onRemove,
  onRemoveMany,
  removingId,
}: GlobalKeywordsListProps) {
  return (
    <Card className="p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Global Prohibited Keywords</h2>
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search keywords..."
          className="pl-8 pr-8"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-sm text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <SelectableProhibitedKeywordList
        items={filteredKeywords}
        totalCount={totalCount}
        isLoading={isLoading}
        scopeLabel="globally"
        layout="wide"
        removingId={removingId}
        onRemove={onRemove}
        onRemoveMany={onRemoveMany}
      />
    </Card>
  );
}
